import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notificationsAPI, employeesAPI } from '../lib/api';
import { Button } from './ui/button';
import { MobileBottomNav } from './MobileBottomNav';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Calendar,
  Users,
  Briefcase,
  Building2,
  Bell,
  Clock,
  LayoutDashboard,
  Menu,
  X,
  LogOut,
  User,
  ChevronRight,
  Settings,
  Award,
  BarChart3,
  Timer,
  ArrowLeftRight,
  MessageSquare,
  Activity,
  Shield,
  ClipboardCheck,
  CalendarOff,
} from 'lucide-react';

const workStatusOptions = [
  { value: 'available', label: 'Available', color: 'bg-emerald-500' },
  { value: 'busy', label: 'Busy', color: 'bg-yellow-500' },
  { value: 'on_break', label: 'On Break', color: 'bg-blue-500' },
  { value: 'off_duty', label: 'Off Duty', color: 'bg-gray-500' },
];

export const Layout = ({ children }) => {
  const { user, logout, updateUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const isAdmin = user?.role === 'admin';

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Schedule', href: '/schedule', icon: Calendar },
    { name: 'Jobs', href: '/jobs', icon: Briefcase },
    { name: 'Shift Swaps', href: '/shift-swaps', icon: ArrowLeftRight },
    { name: 'Time Tracking', href: '/time-tracking', icon: Timer },
    ...(isAdmin ? [
      { name: 'Approvals', href: '/timesheet-approvals', icon: ClipboardCheck },
      { name: 'Reports', href: '/reports', icon: BarChart3 },
      { name: 'Employees', href: '/employees', icon: Users },
      { name: 'Offices', href: '/offices', icon: Building2 },
      { name: 'Certifications', href: '/certifications', icon: Award },
      { name: 'Customers', href: '/customers', icon: Building2 },
    ] : []),
    { name: 'Availability', href: '/availability', icon: Clock },
    { name: 'Team Chat', href: '/chat', icon: MessageSquare },
    { name: 'Notifications', href: '/notifications', icon: Bell },
    { name: 'Time Off Requests', href: '/time-off-requests', icon: CalendarOff },
  ];

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationsAPI.getUnreadCount();
        setUnreadCount(response.data.count);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();
    
    // Listen for real-time notifications to update count
    const handleNewNotification = () => {
      fetchUnreadCount();
    };
    
    window.addEventListener('newNotification', handleNewNotification);
    
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('newNotification', handleNewNotification);
    };
  }, []);

  const handleWorkStatusChange = async (status) => {
    try {
      await employeesAPI.updateWorkStatus(user.id, status);
      updateUser({ work_status: status });
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const currentStatus = workStatusOptions.find(s => s.value === user?.work_status) || workStatusOptions[3];

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700/50">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-heading font-bold text-lg text-white">API Job Board</span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.name}</span>
                  {item.name === 'Notifications' && unreadCount > 0 && (
                    <Badge variant="destructive" className="ml-auto text-xs">
                      {unreadCount}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-slate-700/50">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-slate-800 transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate text-white">{user?.name}</p>
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${currentStatus.color}`} />
                      <span className="text-xs text-slate-400">{currentStatus.label}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Work Status</DropdownMenuLabel>
                {workStatusOptions.map((status) => (
                  <DropdownMenuItem
                    key={status.value}
                    onClick={() => handleWorkStatusChange(status.value)}
                    data-testid={`status-${status.value}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${status.color} mr-2`} />
                    {status.label}
                    {user?.work_status === status.value && (
                      <span className="ml-auto text-primary">✓</span>
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center">
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings/notifications" className="flex items-center" data-testid="notification-settings-link">
                    <Settings className="w-4 h-4 mr-2" />
                    Notification Settings
                  </Link>
                </DropdownMenuItem>
                {user?.role === 'admin' && (
                  <DropdownMenuItem asChild>
                    <Link to="/settings/cost-centers" className="flex items-center" data-testid="cost-centers-link">
                      <Building2 className="w-4 h-4 mr-2" />
                      Divisions
                    </Link>
                  </DropdownMenuItem>
                )}
                {user?.role === 'admin' && (
                  <DropdownMenuItem asChild>
                    <Link to="/settings/notifications-health" className="flex items-center" data-testid="notifications-health-link">
                      <Activity className="w-4 h-4 mr-2" />
                      Notifications Health
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/privacy" className="flex items-center" data-testid="privacy-policy-dropdown-link">
                    <Shield className="w-4 h-4 mr-2" />
                    Privacy Policy
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/sms-terms" className="flex items-center" data-testid="sms-terms-dropdown-link">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    SMS Terms
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="main-content">
        {/* Top bar */}
        <header className="sticky top-0 z-30 glass h-16 flex items-center justify-between px-4 md:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>

          <div className="flex-1" />

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              onClick={() => navigate('/notifications')}
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </Button>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 pb-24 md:p-6 lg:p-8">
          {children}
        </main>
      </div>

      {/* Mobile bottom navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default Layout;
